import { TextField, Typography, Button, Paper } from "@mui/material";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createPost, updatePost } from "../../actions/posts";
import LinearProgress from "@mui/material/LinearProgress";

const Form = ({ currentId, setCurrentId }) => {
  //state for all the data inside our form
  const [postData, setPostData] = useState({
    title: "",
    message: "",
    tags: "",
    selectedFile: "",
  });
  const [progressValue, setProgressValue] = useState(0); //state to store file uploading progress
  const dispatch = useDispatch(); //getting the dispatch function
  const navigate = useNavigate(); //getting the router-dom navigate function = useHistory upgrade

  //find the postToUpdate inside our posts reducer state by comparing with the prop from Post component
  const postToUpdate = useSelector((state) =>
    currentId
      ? state.postsReducer.posts.find((p) => p._id === currentId)
      : null,
  );

  // setting the chosen post inside the form to update it's values
  useEffect(() => {
    if (postToUpdate) {
      setPostData(postToUpdate);
    }
  }, [postToUpdate]);

  //function to handle the form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    let finalImageUrl = postData.selectedFile;

    // Only upload to Cloudinary if 'selectedFile' is a new File object
    // (If it's a string, it means we're editing and keeping the old image)
    if (postData.selectedFile instanceof File) {
      const formData = new FormData();
      formData.append("file", postData.selectedFile);
      formData.append("upload_preset", "insta_uploads");
      formData.append("cloud_name", "omardev93");

      try {
        setProgressValue(50);
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/omardev93/image/upload`,
          { method: "POST", body: formData },
        );
        const data = await response.json();
        finalImageUrl = data.secure_url;
        setProgressValue(100);
      } catch (error) {
        console.error("Cloudinary Upload Error: ", error);
        setProgressValue(0);
        return; // Stop if upload fails
      }
    } else if (!finalImageUrl) {
      alert("Please select an image!");
      return;
    }

    // 3. Dispatch with the final URL
    const userProfile = JSON.parse(localStorage.getItem("profile"));
    const userName = userProfile?.result.name;

    const updatedData = {
      ...postData,
      selectedFile: finalImageUrl,
      name: userName,
    };

    if (currentId) {
      dispatch(updatePost(currentId, updatedData));
    } else {
      dispatch(createPost(updatedData));
      navigate("/");
    }

    setTimeout(() => {
      setProgressValue(0);
      clear();
    }, 1000);
  };

  //if user is not logged , show (Please Sign In) message
  if (!JSON.parse(localStorage.getItem("profile"))?.result?.name) {
    return (
      <Paper sx={{ margin: "10px", padding: "10px" }}>
        <Typography variant="h6" align="center">
          Please Sign In to create your own memories and like other's memories.
        </Typography>
      </Paper>
    );
  }

  const clear = () => {
    //function to clear the form
    setCurrentId(null);
    setPostData({ title: "", message: "", tags: "", selectedFile: "" });
  };

  //creating our form using Material ui components & setting the text fields to our state
  return (
    <Paper sx={{ padding: "10px 20px 10px 10px", borderRadius: "15px" }}>
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}
      >
        <Typography variant="h6">
          {currentId ? "Editing" : "Creating"} a memory
        </Typography>

        <TextField
          name="title"
          variant="outlined"
          label="Title"
          fullWidth
          sx={{ margin: "10px" }}
          value={postData.title}
          onChange={(e) => setPostData({ ...postData, title: e.target.value })}
        />

        <TextField
          name="message"
          variant="outlined"
          label="Message"
          fullWidth
          sx={{ margin: "10px" }}
          multiline
          value={postData.message}
          rows={4}
          onChange={(e) =>
            setPostData({ ...postData, message: e.target.value })
          }
        />

        <TextField
          name="tags"
          variant="outlined"
          label="Tags (coma separated)"
          fullWidth
          sx={{ margin: "10px" }}
          value={postData.tags}
          onChange={(e) =>
            setPostData({
              ...postData,
              tags: e.target.value.split(",").map((s) => s.trim()),
            })
          }
        />

        {/* --- Image Upload Section --- */}
        <div style={{ margin: "10px 0" }}>
          <Button variant="outlined" component="label" color="info" fullWidth>
            {postData.selectedFile ? "Change Image" : "Upload Image"}
            <input type="file" hidden onChange={(e) => setPostData({ ...postData, selectedFile: e.target.files[0] })} />
          </Button>
          
          {postData.selectedFile && (
            <Typography variant="caption" display="block" sx={{ textAlign: "center", mt: 1 }}>
              Selected: {postData.selectedFile instanceof File ? postData.selectedFile.name : "Current Post Image"}
            </Typography>
          )}

          {/* Progress bar appears only when uploading */}
          {progressValue > 0 && (
            <LinearProgress variant="determinate" value={progressValue} sx={{ marginTop: "12px", borderRadius: '5px' }} />
          )}
        </div>
        {/* ---------------------------- */}

        <Button
          variant="contained"
          color="primary"
          size="large"
          type="submit"
          sx={{ marginBottom: "10px", width: "100%" }}
        >
          Submit
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={clear}
          sx={{ marginBottom: "10px", width: "100%" }}
        >
          Clear
        </Button>
      </form>
    </Paper>
  );
};

export default Form;
